'use client';
import { createTheme } from '@mui/material/styles';

import { classes as MuiAccordion } from './components/Accordion/classes';
import { classes as MuiAccordionDetails } from './components/AccordionDetails/classes';
import { classes as MuiAccordionSummary } from './components/AccordionSummary/classes';
import { classes as MuiAutocomplete } from './components/Autocomplete/classes';
import { classes as MuiAvatar } from './components/Avatar/classes';
import { classes as MuiBackdrop } from './components/Backdrop/classes';
import { classes as MuiBadge } from './components/Badge/classes';
import { classes as MuiSnackbar } from './components/Banner/classes';
import { classes as MuiSnackbarContent } from './components/BannerContent/classes';
import { classes as MuiButton } from './components/Button/classes';
import { classes as MuiCard } from './components/Card/classes';
import { classes as MuiCardActions } from './components/CardActions/classes';
import { classes as MuiCardContent } from './components/CardContent/classes';
import { classes as MuiCardHeader } from './components/CardHeader/classes';
import { classes as MuiCardMedia } from './components/CardMedia/classes';
import { classes as MuiCheckbox } from './components/Checkbox/classes';
import { classes as MuiChip } from './components/Chip/classes';
import { classes as MuiCircularProgress } from './components/CircularProgress/classes';
import { classes as MuiDialog } from './components/CustomDialog/classes';
import { classes as MuiAppBar } from './components/CustomNavigationBar/classes';
import { classes as MuiTable } from './components/CustomTable/classes';
import {
  MuiDateCalendar,
  MuiDayCalendar,
  MuiMonthCalendar,
  MuiPickersArrowSwitcher,
  MuiPickersCalendarHeader,
  MuiPickersDay,
  MuiPickersLayout,
  MuiYearCalendar,
} from './components/DatePicker/classes';
import { MuiDateRangePickerDay } from './components/DateRangePicker/classes';
import { classes as MuiDialogActions } from './components/DialogActions/classes';
import { classes as MuiDialogContent } from './components/DialogContent/classes';
import { classes as MuiDialogContentText } from './components/DialogContentText/classes';
import { classes as MuiDivider } from './components/Divider/classes';
import { classes as MuiDrawer } from './components/Drawer/classes';
import { classes as MuiFab } from './components/Fab/classes';
import { classes as MuiFormControlLabel } from './components/FormControlLabel/classes';
import { classes as MuiFormHelperText } from './components/FormHelperText/classes';
import { classes as MuiFormLabel } from './components/FormLabel/classes';
import { classes as MuiIconButton } from './components/IconButton/classes';
import { classes as MuiInput } from './components/Input/classes';
import { classes as MuiInputAdornment } from './components/InputAdornment/classes';
import { classes as MuiInputBase } from './components/InputBase/classes';
import { classes as MuiInputLabel } from './components/InputLabel/classes';
import { classes as MuiLinearProgress } from './components/LinearProgress/classes';
import { classes as MuiList } from './components/List/classes';
import { classes as MuiListItem } from './components/ListItem/classes';
import { classes as MuiListItemText } from './components/ListItemText/classes';
import { classes as MuiMenu } from './components/Menu/classes';
import { classes as MuiMenuItem } from './components/MenuItem/classes';
import { classes as MuiPaper } from './components/Paper/classes';
import { classes as MuiPopover } from './components/Popover/classes';
import { classes as MuiRadio } from './components/Radio/classes';
import { classes as MuiToggleButton } from './components/SegmentedControl/classes';
import { classes as MuiToggleButtonGroup } from './components/SegmentedControlGroup/classes';
import { classes as MuiSelect } from './components/Select/classes';
import { classes as MuiSlider } from './components/Slider/classes';
import { classes as MuiStep } from './components/Step/classes';
import { classes as MuiStepConnector } from './components/StepConnector/classes';
import { classes as MuiStepLabel } from './components/StepLabel/classes';
import { classes as MuiStepper } from './components/Stepper/classes';
import { classes as MuiSvgIcon } from './components/SvgIcon/classes';
import { classes as MuiSwitch } from './components/Switch/classes';
import { classes as MuiTab } from './components/Tab/classes';
import { classes as MuiTableBody } from './components/TableBody/classes';
import { classes as MuiTableCell } from './components/TableCell/classes';
import { classes as MuiTableFooter } from './components/TableFooter/classes';
import { classes as MuiTableHead } from './components/TableHead/classes';
import { classes as MuiTablePagination } from './components/TablePagination/classes';
import { classes as MuiTableRow } from './components/TableRow/classes';
import { classes as MuiTableSortLabel } from './components/TableSortLabel/classes';
import { classes as MuiTabs } from './components/Tabs/classes';
import { classes as MuiTextField } from './components/TextField/classes';
import { classes as MuiTooltip } from './components/Tooltip/classes';
import { classes as MuiTreeItem } from './components/TreeItem/classes';
import { classes as MuiTreeView } from './components/TreeView/classes';
import palette from './palette';
import typography from './typography';
import { createLegacyTokens } from './utils/tokens/createLegacyTokens';
import idsTokens from './utils/tokens/idsTokens';
import idsTokensDark from './utils/tokens/idsTokensDark';
import idsTokensPatientDark from './utils/tokens/idsTokensPatientDark';
import idsPatientGlaukosEpioxa from './utils/tokens/idsTokensPatientGlaukosEpioxa';
import idsTokensPatientGlaukosiDose from './utils/tokens/idsTokensPatientGlaukosiDose';
import idsTokensPatientIndiviorInsupport from './utils/tokens/idsTokensPatientIndiviorInsupport';
import idsTokensPatientLight from './utils/tokens/idsTokensPatientLight';
import idsTokensPatientNovoCare from './utils/tokens/idsTokensPatientNovoCare';
import idsPatientVandaPonvory from './utils/tokens/idsTokensPatientVandaPonvory';
import idsTokensWireframe from './utils/tokens/idsTokensWireframe';

export const overrides = {
  MuiAppBar,
  MuiAutocomplete,
  MuiAvatar,
  MuiBackdrop,
  MuiBadge,
  MuiButton,
  MuiCard,
  MuiCardActions,
  MuiCardContent,
  MuiCardHeader,
  MuiCardMedia,
  MuiCheckbox,
  MuiChip,
  MuiCircularProgress,
  MuiDialog,
  MuiDialogActions,
  MuiDialogContent,
  MuiDialogContentText,
  MuiDivider,
  MuiDrawer,
  MuiAccordion,
  MuiAccordionDetails,
  MuiAccordionSummary,
  MuiFab,
  MuiFormControlLabel,
  MuiFormHelperText,
  MuiFormLabel,
  MuiIconButton,
  MuiInput,
  MuiInputAdornment,
  MuiInputBase,
  MuiInputLabel,
  MuiList,
  MuiListItem,
  MuiListItemText,
  MuiLinearProgress,
  MuiMenu,
  MuiMenuItem,
  MuiPaper,
  MuiPickersArrowSwitcher,
  MuiPickersDay,
  MuiDateCalendar,
  MuiDayCalendar,
  MuiPickersLayout,
  MuiDateRangePickerDay,
  MuiPickersCalendarHeader,
  MuiMonthCalendar,
  MuiYearCalendar,
  MuiRadio,
  MuiSelect,
  MuiSlider,
  MuiSnackbar,
  MuiSnackbarContent,
  MuiStep,
  MuiStepConnector,
  MuiStepLabel,
  MuiStepper,
  MuiSvgIcon,
  MuiSwitch,
  MuiTab,
  MuiTable,
  MuiTableBody,
  MuiTableCell,
  MuiTableFooter,
  MuiTableHead,
  MuiTablePagination,
  MuiTableRow,
  MuiTableSortLabel,
  MuiTabs,
  MuiTextField,
  MuiToggleButton,
  MuiToggleButtonGroup,
  MuiTooltip,
  MuiTreeItem,
  MuiTreeView,
  MuiPopover,
};

export const fixOverrides = (overrides, tokens) => {
  const updatedOverrides = {};

  for (const key in overrides) {
    const value = overrides[key];

    updatedOverrides[key] = {
      styleOverrides: typeof value === 'function' ? value(tokens) : value,
    };
  }

  return updatedOverrides;
};

const tokensMap = {
  IDS: idsTokens,
  IDSLegacy: createLegacyTokens(idsTokens),
  IDSDark: idsTokensDark,
  IDSDarkLegacy: createLegacyTokens(idsTokensDark),
  patientLight: idsTokensPatientLight,
  patientLightLegacy: createLegacyTokens(idsTokensPatientLight),
  patientDark: idsTokensPatientDark,
  patientDarkLegacy: createLegacyTokens(idsTokensPatientDark),
  wireframe: idsTokensWireframe,
  wireframeLegacy: createLegacyTokens(idsTokensWireframe),
  patientGlaukosEpioxa: idsPatientGlaukosEpioxa,
  patientVandaPonvory: idsPatientVandaPonvory,
  patientIndiviorInsupport: idsTokensPatientIndiviorInsupport,
  patientGlaukosiDose: idsTokensPatientGlaukosiDose,
  patientNovoCare: idsTokensPatientNovoCare,
};

export const themeFactory = (themeName = 'IDS') => {
  const tokens = tokensMap[themeName];

  return createTheme({
    components: fixOverrides(overrides, tokens),
    palette,
    typography: typography(tokens),
    legacyThemeName: 'Apollo',
    themeName,
    idsTokens: tokens,
  });
};

export default themeFactory();
